import { getAppDefinition, isAppCatalogVisible, isAppConnectorLive } from '@hero/apps';
import type { FeatureFlagKey } from '@hero/core';
import type { AppId, AppStatus } from '@hero/schema';
import { useFeature } from '@hero/web-context';

/**
 * Resolves rollout flags declared on catalog apps (`AppDefinition.featureFlag`).
 *
 * Add a `useFeature()` line here when a new app declares `featureFlag` in
 * `@hero/apps`. React hooks cannot be called in a loop over
 * `APP_CATALOG_FEATURE_FLAGS`, so this map stays explicit.
 */
function useCatalogFeatureFlagEnabled(): (key: FeatureFlagKey) => boolean {
  const bayan = useFeature('bayan_integration');

  const enabledByKey: Partial<Record<FeatureFlagKey, boolean>> = {
    bayan_integration: bayan.isEnabled,
  };

  return key => enabledByKey[key] ?? true;
}

function resolveAppRollout(
  app: { id: AppId; status: AppStatus },
  isFlagEnabled: (key: FeatureFlagKey) => boolean,
) {
  const definition = getAppDefinition(app.id);
  if (!definition) return undefined;

  return {
    status: app.status,
    featureFlag: definition.featureFlag,
    isFlagEnabled,
  };
}

/** Whether one catalog app's connector is live for the current user/org. */
export function useAppConnectorLive(appId: AppId): boolean {
  const definition = getAppDefinition(appId);
  const isFlagEnabled = useCatalogFeatureFlagEnabled();
  if (!definition) return false;
  return isAppConnectorLive(definition, isFlagEnabled);
}

/** Same as {@link useAppConnectorLive}, for a marketplace card payload. */
export function useAppConnectorLiveForView(app: {
  id: AppId;
  status: AppStatus;
}): boolean {
  const rollout = resolveAppRollout(app, useCatalogFeatureFlagEnabled());
  if (!rollout) return false;
  return isAppConnectorLive(rollout, rollout.isFlagEnabled);
}

/**
 * Returns a stable checker for catalog cards — call the hook once, then use
 * the returned function inside lists/maps without violating Rules of Hooks.
 */
export function useAppConnectorLiveCheck(): (app: {
  id: AppId;
  status: AppStatus;
}) => boolean {
  const isFlagEnabled = useCatalogFeatureFlagEnabled();

  return app => {
    const rollout = resolveAppRollout(app, isFlagEnabled);
    if (!rollout) return false;
    return isAppConnectorLive(rollout, isFlagEnabled);
  };
}

/**
 * Whether a marketplace card should render — hides `inactive` and rollout-gated apps.
 */
export function useAppCatalogVisibleCheck(): (app: {
  id: AppId;
  status: AppStatus;
}) => boolean {
  const isFlagEnabled = useCatalogFeatureFlagEnabled();

  return app => {
    const rollout = resolveAppRollout(app, isFlagEnabled);
    if (!rollout) return false;
    return isAppCatalogVisible(rollout, isFlagEnabled);
  };
}
